<template>
    <div class="group-chat-container">
        <!-- ✅ En-tête du chat amélioré -->
        <v-card class="chat-header elevation-2">
            <v-layout row wrap align-center class="pa-3">
                <!-- Bouton retour avec badge -->


                <!-- Info du groupe -->
                <v-flex xs9 sm9>
                    <div class="group-info">
                        <h3 class="group-name mb-0">{{ groupName }}</h3>
                        <div class="group-meta">
                            <v-icon x-small class="mr-1">group</v-icon>
                            <small class="grey--text">{{ membersCount }} members</small>
                            <span v-if="isTyping" class="typing-indicator ml-2">
                                <v-icon x-small color="success">fiber_manual_record</v-icon>
                                Someone is typing...
                            </span>
                        </div>
                    </div>
                </v-flex>

                <!-- Actions -->
                <v-flex xs2 class="text-xs-right">
                    <v-btn icon @click="showMembersDialog = true" class="members-btn">
                        <v-badge color="primary" :value="membersCount > 0" :content="membersCount">
                            <v-icon>group</v-icon>
                        </v-badge>
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-card>

        <!-- ✅ Zone des messages optimisée -->
        <div class="messages-container" ref="messagesContainer" @scroll="handleScroll">
            <!-- Loading -->
            <div v-if="isLoadingMessages" class="loading-state">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <p class="grey--text mt-3">Loading messages...</p>
            </div>

            <!-- Empty state -->
            <div v-else-if="messages.length === 0" class="empty-state">
                <div class="empty-illustration">
                    <v-icon size="80" color="grey lighten-2">chat_bubble_outline</v-icon>
                </div>
                <h3 class="grey--text text--darken-1 mt-3">No messages yet</h3>
                <p class="grey--text">Start the conversation and say hello! 👋</p>
            </div>

            <!-- Messages list -->
            <div v-else class="messages-list pa-3">
                <div
                    v-for="(message, index) in messages"
                    :key="message.id"
                    :class="['message-wrapper', { 'show-sender': shouldShowSender(message, index) }]"
                >
                    <!-- Date separator -->
                    <div v-if="shouldShowDateSeparator(message, index)" class="date-separator">
                        <span>{{ formatDate(message.timestamp) }}</span>
                    </div>

                    <!-- Message bubble -->
                    <div :class="['message-bubble', message.sender.is_me ? 'my-message' : 'other-message']">
                        <!-- Avatar pour les autres -->
                        <v-avatar
                            v-if="!message.sender.is_me && shouldShowSender(message, index)"
                            size="32"
                            color="primary lighten-4"
                            class="message-avatar"
                        >
                            <span class="primary--text font-weight-bold">
                                {{ message.sender.username.charAt(0).toUpperCase() }}
                            </span>
                        </v-avatar>

                        <!-- Contenu -->
                        <div class="bubble-wrapper">
                            <!-- Nom de l'expéditeur -->
                            <div v-if="!message.sender.is_me && shouldShowSender(message, index)" class="sender-name">
                                {{ message.sender.username }}
                            </div>

                            <!-- Message content -->
                            <div class="message-content">
                                <!-- Texte -->
                                <p v-if="message.content" class="message-text mb-0">{{ message.content }}</p>

                                <!-- Image -->
                                <div
                                    v-if="message.file && message.message_type === 'image'"
                                    class="image-container"
                                    :class="{
                                        'error': imageErrors[message.file],
                                        'mt-2': message.content
                                    }"
                                >
                                    <img
                                        v-if="!imageErrors[message.file]"
                                        :src="message.file"
                                        alt="Image"
                                        class="message-image"
                                        @click="openImageModal(message.file)"
                                        @error="handleImageError(message.file)"
                                    />
                                    <div v-else class="image-error">
                                        <v-icon color="grey lighten-1">broken_image</v-icon>
                                        <span>Image unavailable</span>
                                    </div>
                                </div>



                                <!-- Meta info -->
                                <div class="message-meta">
                                    <span class="timestamp">{{ formatTime(message.timestamp) }}</span>
                                    <v-chip v-if="message.is_edited" x-small class="edited-chip ml-1">Edited</v-chip>
                                    <v-icon v-if="message.sender.is_me" x-small class="ml-1 status-icon">done_all</v-icon>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Scroll to bottom FAB (sans badge) -->
            <transition name="fade">
                <v-btn
                    v-if="showScrollButton"
                    fab
                    small
                    color="white"
                    class="scroll-to-bottom"
                    @click="scrollToBottom(true)"
                >
                    <v-icon color="primary">keyboard_arrow_down</v-icon>
                </v-btn>
            </transition>
        </div>

        <!-- ✅ Zone de saisie moderne -->
        <v-card class="message-input-container elevation-4">
            <!-- File preview -->
            <transition name="slide-up">
                <div v-if="selectedFile" class="file-preview-banner">
                    <div class="preview-content">
                        <v-icon small color="primary">image</v-icon>
                        <span class="preview-text">{{ selectedFile.name }}</span>
                        <v-btn icon x-small @click="removeFile" class="remove-btn">
                            <v-icon x-small>close</v-icon>
                        </v-btn>
                    </div>
                </div>
            </transition>

            <!-- Input area -->
            <v-layout row wrap align-center class="input-area pa-2">
                <!-- Attach button -->
                <v-flex shrink>
                    <v-menu offset-y top>
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" :disabled="isSending">
                                <v-icon color="grey darken-1">attach_file</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile @click="$refs.imageInput.click()">
                                <v-list-tile-avatar>
                                    <v-icon color="primary">image</v-icon>
                                </v-list-tile-avatar>
                                <v-list-tile-content>Photo</v-list-tile-content>
                            </v-list-tile>

                        </v-list>
                    </v-menu>
                    <input
                        ref="imageInput"
                        type="file"
                        style="display: none"
                        @change="handleFileSelect"
                        accept="image/*"
                    >
                    <input
                        ref="videoInput"
                        type="file"
                        style="display: none"
                        @change="handleFileSelect"
                        accept="video/*"
                    >
                </v-flex>

                <!-- Text input -->
                <v-flex grow>
                    <v-text-field
                        v-model="newMessage"
                        placeholder="Type a message..."
                        solo
                        flat
                        hide-details
                        class="message-field"
                        @keyup.enter="sendMessage"
                        @input="handleTyping"
                        :disabled="isSending"
                    ></v-text-field>
                </v-flex>

                <!-- Send button -->
                <v-flex shrink>
                    <v-btn
                        fab
                        small
                        color="primary"
                        @click="sendMessage"
                        :disabled="!canSendMessage || isSending"
                        :loading="isSending"
                        class="send-btn"
                    >
                        <v-icon>send</v-icon>
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-card>

        <!-- ✅ Dialog des membres moderne -->
        <v-dialog v-model="showMembersDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline primary white--text">
                    <v-icon left color="white">group</v-icon>
                    Group Members ({{ members.length }})
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-list v-if="!isLoadingMembers" two-line>
                        <v-list-tile
                            v-for="member in members"
                            :key="member.id"
                            avatar
                        >
                            <v-list-tile-avatar color="primary lighten-4">
                                <span class="primary--text font-weight-bold">
                                    {{ member.username.charAt(0).toUpperCase() }}
                                </span>
                            </v-list-tile-avatar>

                            <v-list-tile-content>
                                <v-list-tile-title class="font-weight-medium">
                                    {{ member.username }}
                                </v-list-tile-title>
                                <v-list-tile-sub-title>
                                    <v-chip v-if="member.is_me" x-small color="success" text-color="white">
                                        You
                                    </v-chip>
                                    <v-chip v-if="member.is_admin" x-small color="warning" text-color="white">
                                        Admin
                                    </v-chip>
                                    <span v-if="member.position" class="ml-1">Position #{{ member.position }}</span>
                                </v-list-tile-sub-title>
                            </v-list-tile-content>

                            <v-list-tile-action>
                                <v-icon small color="success">circle</v-icon>
                            </v-list-tile-action>
                        </v-list-tile>
                    </v-list>

                    <div v-else class="pa-4 text-xs-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" flat @click="showMembersDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ✅ Image Modal élégant -->
        <v-dialog v-model="showImageModal" max-width="90vw" content-class="image-dialog">
            <v-card class="image-modal elevation-24">
                <v-btn
                    icon
                    absolute
                    top
                    right
                    @click="closeImageModal"
                    class="close-modal-btn"
                    dark
                >
                    <v-icon>close</v-icon>
                </v-btn>
                <img
                    :src="selectedImageUrl"
                    alt="Full size image"
                    class="modal-image"
                />
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            groupId: null,
            groupName: '',
            conversationId: null,
            membersCount: 0,
            messages: [],
            members: [],
            newMessage: '',
            selectedFile: null,
            isLoadingMessages: true,
            isLoadingMembers: false,
            isSending: false,
            showMembersDialog: false,
            messageRefreshInterval: null,
            imageErrors: {},
            videoErrors: {},
            showImageModal: false,
            selectedImageUrl: '',
            showScrollButton: false,
            unreadCount: 0,
            isTyping: false,
            typingTimeout: null,
            lastSeenMessageId: null, // Pour tracker le dernier message vu
        }
    },

    computed: {
        canSendMessage() {
            return (this.newMessage.trim() !== '' || this.selectedFile) && !this.isSending
        }
    },

    methods: {
        async loadMessages() {
            this.isLoadingMessages = true

            try {
                const response = await axios.get(`/api/v1/njangi/groups/${this.groupId}/chat/`)

                this.conversationId = response.data.conversation_id
                this.groupName = response.data.group_name
                this.membersCount = response.data.members_count
                this.messages = response.data.messages

                // Précharger le tracking des images et vidéos
                this.messages.forEach(msg => {
                    if (msg.message_type === 'image' && msg.file && !this.imageErrors[msg.file]) {
                        this.$set(this.imageErrors, msg.file, false)
                    }
                    if (msg.message_type === 'video' && msg.file && !this.videoErrors[msg.file]) {
                        this.$set(this.videoErrors, msg.file, false)
                    }
                })

                console.log('✅ Messages loaded:', this.messages.length)

                this.$nextTick(() => {
                    this.scrollToBottom(true)
                    this.updateLastSeenMessage()
                })
            } catch (error) {
                console.error('Error loading messages:', error)

                if (error.response && error.response.status === 403) {
                    this.$store.commit('setSnackBarMessage', "You are not a member of this group")
                    this.$store.commit('setSnackBarColor', "error")
                    this.$store.commit('activateSnackBar', true)
                    this.goBack()
                } else {
                    this.$store.commit('setSnackBarMessage', "Error loading messages")
                    this.$store.commit('setSnackBarColor', "error")
                    this.$store.commit('activateSnackBar', true)
                }
            }

            this.isLoadingMessages = false
        },

        async sendMessage() {
            if (!this.canSendMessage) return

            this.isSending = true

            try {
                const formData = new FormData()
                formData.append('content', this.newMessage.trim())

                if (this.selectedFile) {
                    formData.append('file', this.selectedFile)

                    // Déterminer le type automatiquement
                    if (this.selectedFile.type.startsWith('video/')) {
                        formData.append('message_type', 'video')
                    } else if (this.selectedFile.type.startsWith('image/')) {
                        formData.append('message_type', 'image')
                    } else {
                        formData.append('message_type', 'file')
                    }
                } else {
                    formData.append('message_type', 'text')
                }

                console.log('📤 Sending message...')

                const response = await axios.post(
                    `/api/v1/njangi/groups/${this.groupId}/chat/`,
                    formData,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        timeout: 60000 // 60 secondes pour vidéos
                    }
                )

                console.log('✅ Message sent:', response.data)

                this.messages.push(response.data)

                if (response.data.message_type === 'image' && response.data.file) {
                    this.$set(this.imageErrors, response.data.file, false)
                }
                if (response.data.message_type === 'video' && response.data.file) {
                    this.$set(this.videoErrors, response.data.file, false)
                }

                this.newMessage = ''
                this.selectedFile = null

                this.$nextTick(() => {
                    this.scrollToBottom(true)
                    this.updateLastSeenMessage()
                })
            } catch (error) {
                console.error('❌ Error sending message:', error)
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                })

                this.$store.commit('setSnackBarMessage',
                    error.response?.data?.error || "Error sending message")
                this.$store.commit('setSnackBarColor', "error")
                this.$store.commit('activateSnackBar', true)
            }

            this.isSending = false
        },

        async loadMembers() {
            this.isLoadingMembers = true

            try {
                const response = await axios.get(`/api/v1/njangi/groups/${this.groupId}/chat/members/`)
                this.members = response.data.members
                console.log('✅ Members loaded:', this.members.length)
            } catch (error) {
                console.error('Error loading members:', error)
            }

            this.isLoadingMembers = false
        },

        async markAsRead() {
            try {
                await axios.post(`/api/v1/njangi/groups/${this.groupId}/chat/mark-read/`)
                console.log('✅ Chat marked as read')
                this.unreadCount = 0
            } catch (error) {
                console.error('Error marking as read:', error)
            }
        },

        handleFileSelect(event) {
            const file = event.target.files[0]
            if (!file) return

            const isVideo = file.type.startsWith('video/')
            const isImage = file.type.startsWith('image/')

            // Limites de taille
            const maxSize = isVideo ? 100 * 1024 * 1024 : 5 * 1024 * 1024
            const maxSizeMB = maxSize / (1024 * 1024)

            if (file.size > maxSize) {
                this.$store.commit('setSnackBarMessage', `File too large (max ${maxSizeMB}MB)`)
                this.$store.commit('setSnackBarColor', "error")
                this.$store.commit('activateSnackBar', true)
                return
            }

            this.selectedFile = file
            console.log('📎 File selected:', {
                name: file.name,
                type: file.type,
                size: `${(file.size / 1024 / 1024).toFixed(2)} MB`
            })
        },

        removeFile() {
            this.selectedFile = null
        },

        handleTyping() {
            // Simuler l'indicateur "typing" (à implémenter avec WebSocket plus tard)
            if (this.typingTimeout) clearTimeout(this.typingTimeout)
        },

        handleScroll() {
            const container = this.$refs.messagesContainer
            if (container) {
                const threshold = 100
                const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold
                this.showScrollButton = !isAtBottom

                // Si on est en bas, réinitialiser le compteur non lus
                if (isAtBottom) {
                    this.updateLastSeenMessage()
                }
            }
        },

        scrollToBottom(smooth = false) {
            const container = this.$refs.messagesContainer
            if (container) {
                if (smooth) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'smooth'
                    })
                } else {
                    container.scrollTop = container.scrollHeight
                }

                // Réinitialiser le compteur après le scroll
                this.$nextTick(() => {
                    this.updateLastSeenMessage()
                })
            }
        },

        updateLastSeenMessage() {
            if (this.messages.length > 0) {
                this.lastSeenMessageId = this.messages[this.messages.length - 1].id
                this.unreadCount = 0
            }
        },

        calculateUnreadCount() {
            if (!this.lastSeenMessageId || this.messages.length === 0) {
                return 0
            }

            const lastSeenIndex = this.messages.findIndex(m => m.id === this.lastSeenMessageId)
            if (lastSeenIndex === -1) {
                return this.messages.length
            }

            // Compter uniquement les messages des autres utilisateurs
            return this.messages.slice(lastSeenIndex + 1).filter(m => !m.sender.is_me).length
        },

        shouldShowSender(message, index) {
            if (index === 0) return true
            const prevMsg = this.messages[index - 1]
            return prevMsg.sender.username !== message.sender.username
        },

        shouldShowDateSeparator(message, index) {
            if (index === 0) return true
            const prevMsg = this.messages[index - 1]
            const currDate = new Date(message.timestamp).toDateString()
            const prevDate = new Date(prevMsg.timestamp).toDateString()
            return currDate !== prevDate
        },

        formatDate(timestamp) {
            const date = new Date(timestamp)
            const today = new Date()
            const yesterday = new Date(today)
            yesterday.setDate(yesterday.getDate() - 1)

            if (date.toDateString() === today.toDateString()) {
                return 'Today'
            } else if (date.toDateString() === yesterday.toDateString()) {
                return 'Yesterday'
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
                })
            }
        },

        formatTime(timestamp) {
            const date = new Date(timestamp)
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            })
        },

        formatTimestamp(timestamp) {
            const date = new Date(timestamp)
            const now = new Date()
            const diffMs = now - date
            const diffMins = Math.floor(diffMs / 60000)

            if (diffMins < 1) return 'Just now'
            if (diffMins < 60) return `${diffMins}m ago`

            const diffHours = Math.floor(diffMins / 60)
            if (diffHours < 24) return `${diffHours}h ago`

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        goBack() {
            this.$router.push({ name: 'njangi_groups.list', params: { category: 'Active' } })
        },

        async refreshMessages() {
            try {
                const response = await axios.get(`/api/v1/njangi/groups/${this.groupId}/chat/`)

                const existingIds = new Set(this.messages.map(m => m.id))
                const newMessages = response.data.messages.filter(m => !existingIds.has(m.id))

                if (newMessages.length > 0) {
                    newMessages.forEach(msg => {
                        if (msg.message_type === 'image' && msg.file && !this.imageErrors[msg.file]) {
                            this.$set(this.imageErrors, msg.file, false)
                        }
                    })

                    this.messages.push(...newMessages)
                    console.log(`✅ ${newMessages.length} new message(s) received`)

                    // Calculer les messages non lus
                    this.unreadCount = this.calculateUnreadCount()

                    // Scroll automatique si on est déjà en bas
                    const container = this.$refs.messagesContainer
                    if (container) {
                        const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 150
                        if (isNearBottom) {
                            this.$nextTick(() => {
                                this.scrollToBottom(true)
                            })
                        }
                    }
                }
            } catch (error) {
                console.error('Error refreshing messages:', error)
            }
        },

        handleImageError(imageUrl) {
            console.error('Image load error:', imageUrl)
            this.$set(this.imageErrors, imageUrl, true)

            setTimeout(() => {
                if (this.imageErrors[imageUrl]) {
                    this.$delete(this.imageErrors, imageUrl)
                }
            }, 2000)
        },

        handleVideoError(videoUrl) {
            console.error('Video load error:', videoUrl)
            this.$set(this.videoErrors, videoUrl, true)
        },

        openImageModal(imageUrl) {
            if (!this.imageErrors[imageUrl]) {
                this.selectedImageUrl = imageUrl
                this.showImageModal = true
            }
        },

        closeImageModal() {
            this.showImageModal = false
            this.selectedImageUrl = ''
        }
    },

    watch: {
        showMembersDialog(val) {
            if (val && this.members.length === 0) {
                this.loadMembers()
            }
        }
    },

    async mounted() {
        this.groupId = this.$route.params.group_id
        document.title = "PettyCash | Group Chat"

        await this.loadMessages()
        this.markAsRead()

        this.messageRefreshInterval = setInterval(() => {
            this.refreshMessages()
        }, 5000)
    },

    beforeDestroy() {
        this.markAsRead()

        if (this.messageRefreshInterval) {
            clearInterval(this.messageRefreshInterval)
        }
    }
}
</script>

<style scoped>
/* Container */
.group-chat-container {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 64px);
    max-width: 1000px;
    margin: 0 auto;
    background: #f5f7fa;
}

/* Header */
.chat-header {
    flex-shrink: 0;
    border-radius: 0;
    background: white;
    border-bottom: 1px solid #e0e0e0;
}

.back-btn {
    transition: transform 0.2s ease;
}

.back-btn:hover {
    transform: translateX(-2px);
}

.group-info {
    display: flex;
    flex-direction: column;
}

.group-name {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
}

.group-meta {
    display: flex;
    align-items: center;
    font-size: 13px;
    margin-top: 2px;
}

.typing-indicator {
    color: #4caf50;
    font-size: 12px;
    font-style: italic;
}

/* Messages container */
.messages-container {
    flex: 1;
    overflow-y: auto;
    background: linear-gradient(135deg, #e3f2fd 0%, #f5f7fa 100%);
    position: relative;
    scroll-behavior: smooth;
}

.messages-container::-webkit-scrollbar {
    width: 6px;
}

.messages-container::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

/* Loading & Empty states */
.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.empty-illustration {
    background: white;
    border-radius: 50%;
    padding: 30px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Messages list */
.messages-list {
    padding: 20px 16px 80px;
}

/* Date separator */
.date-separator {
    display: flex;
    justify-content: center;
    margin: 20px 0;
}

.date-separator span {
    background: rgba(255, 255, 255, 0.9);
    color: #757575;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Message bubble */
.message-wrapper {
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
}

.message-wrapper.show-sender {
    margin-top: 12px;
}

.message-bubble {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    max-width: 75%;
    animation: slideIn 0.3s ease;
}

.my-message {
    align-self: flex-end;
    flex-direction: row-reverse;
}

.other-message {
    align-self: flex-start;
}

.message-avatar {
    flex-shrink: 0;
}

.bubble-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.sender-name {
    font-size: 12px;
    font-weight: 600;
    color: #1976d2;
    margin-bottom: 4px;
    padding-left: 12px;
}

/* Message content */
.message-content {
    background: white;
    padding: 10px 14px;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.message-content:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.my-message .message-content {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;
    border-bottom-right-radius: 6px;
}

.other-message .message-content {
    border-bottom-left-radius: 6px;
}

.message-text {
    font-size: 15px;
    line-height: 1.5;
    word-wrap: break-word;
    white-space: pre-wrap;
}

.my-message .message-text {
    color: white;
}

/* Message meta */
.message-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    font-size: 11px;
    opacity: 0.7;
}

.timestamp {
    font-size: 11px;
}

.edited-chip {
    height: 16px !important;
    font-size: 10px !important;
}

.status-icon {
    font-size: 14px !important;
}

/* Image container */
.image-container {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: #f5f5f5;
    min-height: 120px;
    max-width: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.message-image {
    width: 100%;
    height: auto;
    max-height: 300px;
    object-fit: cover;
    cursor: pointer;
    transition: transform 0.2s ease;
    display: block;
}

.message-image:hover {
    transform: scale(1.02);
}

.image-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 20px;
    color: #999;
    font-size: 12px;
}

.image-container.error {
    background: #fafafa;
    border: 2px dashed #e0e0e0;
}

.my-message .image-container {
    background: rgba(255, 255, 255, 0.1);
}

/* ✅ Video container */
.video-container {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: #000;
    max-width: 350px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.message-video {
    width: 100%;
    max-height: 400px;
    display: block;
    border-radius: 12px;
}

.video-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 20px;
    color: #999;
    font-size: 12px;
    background: #fafafa;
}

.video-container.error {
    background: #fafafa;
    border: 2px dashed #e0e0e0;
}

.my-message .video-container {
    background: rgba(0, 0, 0, 0.8);
}

/* Scroll to bottom button (sans badge maintenant) */
.scroll-to-bottom {
    position: absolute !important;
    bottom: 90px;
    right: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

/* Input container */
.message-input-container {
    flex-shrink: 0;
    border-radius: 0;
    background: white;
    border-top: 1px solid #e0e0e0;
}

.file-preview-banner {
    background: #f5f7fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 8px 16px;
}

.preview-content {
    display: flex;
    align-items: center;
    gap: 8px;
    background: white;
    padding: 8px 12px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-text {
    flex: 1;
    font-size: 13px;
    color: #2c3e50;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.input-area {
    padding: 12px 16px !important;
}

.message-field >>> .v-input__control {
    min-height: 40px !important;
}

.message-field >>> .v-input__slot {
    padding: 0 16px !important;
    background: #f8f9fa !important;
    border-radius: 20px !important;
}

.message-field >>> input {
    font-size: 14px;
}

.send-btn {
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3) !important;
}

/* Image modal */
.image-dialog {
    background: rgba(0, 0, 0, 0.9) !important;
}

.image-modal {
    background: transparent !important;
    box-shadow: none !important;
    overflow: hidden;
}

.modal-image {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
}

.close-modal-btn {
    background: rgba(0, 0, 0, 0.6) !important;
    z-index: 10;
}

/* Animations */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter, .slide-up-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
    .group-chat-container {
        height: calc(100vh - 56px);
    }

    .group-name {
        font-size: 16px;
    }

    .group-meta {
        font-size: 11px;
    }

    .message-bubble {
        max-width: 85%;
    }

    .image-container {
        max-width: 220px;
    }

    .message-image {
        max-height: 240px;
    }

    .scroll-to-bottom {
        bottom: 80px !important;
        right: 16px !important;
    }

    .sender-name {
        font-size: 11px;
    }

    .message-text {
        font-size: 14px;
    }

    .input-area {
        padding: 8px 12px !important;
    }
}

@media (max-width: 400px) {
    .message-bubble {
        max-width: 90%;
    }

    .group-name {
        font-size: 15px;
    }

    .image-container {
        max-width: 200px;
    }
}
</style>